'use client';

import React, {
  FC,
  useState,
  useRef,
  useEffect,
  useMemo,
  KeyboardEvent,
  createContext,
  useContext,
} from 'react';
import styles from './askDebita.module.sass';

// Types

export type MessageRole = 'user' | 'assistant';

/** Status for semantic coloring */
export type SemanticStatus = 'ok' | 'warning' | 'critical' | 'neutral';

/** A single metric rendered as a mini-card inside AI responses */
export interface RichMetric {
  type: 'metric';
  label: string;
  value: string;
  status?: SemanticStatus;
  /** Optional delta — e.g. "+2.5%" */
  delta?: string;
  /** Sparkline data points (0-1 normalized) */
  sparkline?: number[];
  /** If set, hovering this card highlights matching elements on the facility page */
  highlightKey?: string;
}

/** A row in a rich table */
export interface RichTableRow {
  cells: string[];
  status?: SemanticStatus;
}

/** Table card inside AI responses */
export interface RichTable {
  type: 'table';
  headers: string[];
  rows: RichTableRow[];
  highlightKey?: string;
}

/** Timeline event — coupon payments, milestones */
export interface RichTimelineEvent {
  date: string;
  label: string;
  status: SemanticStatus;
  amount?: string;
}

export interface RichTimeline {
  type: 'timeline';
  events: RichTimelineEvent[];
  highlightKey?: string;
}

/** Flow diagram — facility structure */
export interface RichFlow {
  type: 'flow';
  steps: { label: string; sub?: string }[];
  highlightKey?: string;
}

/** Plain text block */
export interface RichText {
  type: 'text';
  content: string;
}

export type RichBlock =
  | RichMetric
  | RichTable
  | RichTimeline
  | RichFlow
  | RichText;

export interface Message {
  id: string;
  role: MessageRole;
  /** For user messages: plain string. For assistant: string OR rich blocks. */
  content: string | RichBlock[];
  timestamp: Date;
}

/** Proactive insight surfaced by the AI without user asking */
export interface Insight {
  id: string;
  severity: SemanticStatus;
  title: string;
  detail: string;
  /** Key to highlight on the facility page */
  highlightKey?: string;
  /** Suggested question to ask */
  action?: string;
}

/** Command for the slash-command palette */
export interface SlashCommand {
  command: string;
  label: string;
  description: string;
}

// Highlight sync context

interface HighlightCtx {
  activeHighlight: string | null;
  setActiveHighlight: (key: string | null) => void;
  /** Text selected by user on the facility page for Select-to-Ask */
  selectedText: string | null;
  selectionRect: { top: number; left: number } | null;
}

const HighlightContext = createContext<HighlightCtx>({
  activeHighlight: null,
  setActiveHighlight: () => {},
  selectedText: null,
  selectionRect: null,
});

/** Wrap any facility-page element with this to make it highlightable by the AI */
export const Highlightable: FC<{
  highlightKey: string;
  children: React.ReactNode;
}> = ({ highlightKey, children }) => {
  const { activeHighlight } = useContext(HighlightContext);
  const isActive = activeHighlight === highlightKey;

  return (
    <span
      className={`${styles.highlightable} ${isActive ? styles.highlighted : ''}`}
      data-highlight-key={highlightKey}
    >
      {children}
    </span>
  );
};

export interface AskDebitaProps {
  /** Whether the panel is expanded */
  open: boolean;
  /** Callback to toggle the panel */
  onClose: () => void;
  /** Async function — receives message string, returns rich blocks or plain string */
  onSend: (message: string) => Promise<string | RichBlock[]>;
  /** Content to render in the main (left) area — the facility page */
  children?: React.ReactNode;
  /** Initial messages */
  initialMessages?: Message[];
  /** Placeholder text */
  placeholder?: string;
  /** Context label (e.g. facility name) */
  contextLabel?: string;
  /** Proactive insights the AI has detected */
  insights?: Insight[];
  /** Callback when an insight is dismissed */
  onDismissInsight?: (id: string) => void;
  /** Available slash commands */
  commands?: SlashCommand[];
  /** Sent automatically when it changes while the panel is open. A value set
   *  while closed is dropped, not queued for the next open. */
  autoSendMessage?: string | null;
}

// Icons

const IconChevronRight = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const IconChevronLeft = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconSend = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const IconBolt = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const IconSlash = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="7" y1="20" x2="17" y2="4" />
  </svg>
);

const IconX = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const IconArrowUp = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

/** Debita isologo — "D" with brand gradient (#5200ff → #3ee8f8) */
const DebitaLogo = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" fill="none">
    <defs>
      <linearGradient
        id="debita-grad"
        x1="11.6"
        y1="221.1"
        x2="424.2"
        y2="221.1"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#5200ff" />
        <stop offset="1" stopColor="#3ee8f8" />
      </linearGradient>
    </defs>
    <g transform="translate(68, 70) scale(0.825)">
      <path
        fill="url(#debita-grad)"
        d="M80.2,68.6h195.4c44.5,0,82.8,8.6,114.7,25.9-16.9-26.3-39.6-46.9-67.9-61.8-32-17.5-70.5-26.3-115.3-26.3H11.6v429.2h68.6V68.6Z"
      />
      <path
        fill="currentColor"
        d="M473.5,176.2c-17.5-32-42.5-56.6-75-73.7-.2-.1-.4-.2-.7-.3-31.9-17.3-70.1-25.9-114.7-25.9H87.8v429.2h195.4c44.8,0,83.3-8.5,115.3-25.6,32.5-17.5,57.4-42.3,75-74.3,17.9-32,26.9-70.3,26.9-114.7s-9-82.6-26.9-114.7ZM412,379.9c-10.2,22.2-26.1,38.4-47.4,48.7-20.9,9.8-48,14.7-81.4,14.7h-126.9V138.4h126.9c24.4,0,45.4,2.8,63.1,8.2,6.5,2,12.6,4.4,18.2,7.1,21.4,9.8,37.2,25.8,47.4,48,9.3,20.1,14.4,46.4,15.2,78.9,0,3.3.1,6.7.1,10.2,0,36.7-5.1,66.4-15.4,89.1Z"
      />
    </g>
  </svg>
);

// Helpers

let _id = 0;
const uid = () => `msg_${++_id}_${Date.now()}`;

const STATUS_COLORS: Record<SemanticStatus, string> = {
  ok: '#059669',
  warning: '#d97706',
  critical: '#dc2626',
  neutral: '#6b7280',
};

const STATUS_CLASSES: Record<SemanticStatus, string> = {
  ok: 'bg-[#059669]',
  warning: 'bg-[#d97706]',
  critical: 'bg-[#dc2626]',
  neutral: 'bg-[#6b7280]',
};

const STATUS_TEXT_CLASSES: Record<SemanticStatus, string> = {
  ok: 'text-[#059669]',
  warning: 'text-[#d97706]',
  critical: 'text-[#dc2626]',
  neutral: 'text-[#6b7280]',
};

// Rich block renderers

/** Mini sparkline SVG */
const Sparkline: FC<{ data: number[]; color?: string }> = ({
  data,
  color = '#5200ff',
}) => {
  const w = 60;
  const h = 20;
  const points = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - v * h}`)
    .join(' ');
  return (
    <svg width={w} height={h} className={styles.sparkline}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const MetricCard: FC<{
  block: RichMetric;
  onHover: (k: string | null) => void;
}> = ({ block, onHover }) => {
  const textClass = block.status ? STATUS_TEXT_CLASSES[block.status] : undefined;
  const sparklineColor = block.status ? STATUS_COLORS[block.status] : undefined;
  return (
    <div
      className={styles.richMetric}
      onMouseEnter={() => block.highlightKey && onHover(block.highlightKey)}
      onMouseLeave={() => onHover(null)}
    >
      <div className={styles.richMetricLabel}>{block.label}</div>
      <div className={`${styles.richMetricValue}${textClass ? ` ${textClass}` : ''}`}>
        {block.value}
        {block.delta && (
          <span className={`${styles.richMetricDelta}${textClass ? ` ${textClass}` : ''}`}>
            {block.delta}
          </span>
        )}
      </div>
      {block.sparkline && <Sparkline data={block.sparkline} color={sparklineColor} />}
    </div>
  );
};

const TableCard: FC<{
  block: RichTable;
  onHover: (k: string | null) => void;
}> = ({ block, onHover }) => (
  <div
    className={styles.richTable}
    onMouseEnter={() => block.highlightKey && onHover(block.highlightKey)}
    onMouseLeave={() => onHover(null)}
  >
    <table>
      <thead>
        <tr>
          {block.headers.map(h => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {block.rows.map((row, i) => (
          <tr key={i}>
            {row.cells.map((cell, j) => (
              <td
                key={j}
                className={
                  j === row.cells.length - 1 && row.status
                    ? STATUS_TEXT_CLASSES[row.status]
                    : undefined
                }
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TimelineCard: FC<{
  block: RichTimeline;
  onHover: (k: string | null) => void;
}> = ({ block, onHover }) => (
  <div
    className={styles.richTimeline}
    onMouseEnter={() => block.highlightKey && onHover(block.highlightKey)}
    onMouseLeave={() => onHover(null)}
  >
    {block.events.map((ev, i) => (
      <div key={i} className={styles.timelineEvent}>
        <div
          className={`${styles.timelineDot} ${STATUS_CLASSES[ev.status]}`}
        />
        <div className={styles.timelineContent}>
          <div className={styles.timelineDate}>{ev.date}</div>
          <div className={styles.timelineLabel}>{ev.label}</div>
          {ev.amount && (
            <div className={styles.timelineAmount}>{ev.amount}</div>
          )}
        </div>
      </div>
    ))}
  </div>
);

const FlowCard: FC<{
  block: RichFlow;
  onHover: (k: string | null) => void;
}> = ({ block, onHover }) => (
  <div
    className={styles.richFlow}
    onMouseEnter={() => block.highlightKey && onHover(block.highlightKey)}
    onMouseLeave={() => onHover(null)}
  >
    {block.steps.map((step, i) => (
      <React.Fragment key={i}>
        {i > 0 && (
          <div className={styles.flowArrow}>
            <IconArrowUp />
          </div>
        )}
        <div className={styles.flowStep}>
          <div className={styles.flowStepLabel}>{step.label}</div>
          {step.sub && <div className={styles.flowStepSub}>{step.sub}</div>}
        </div>
      </React.Fragment>
    ))}
  </div>
);

/** Render one rich block */
const BlockRenderer: FC<{
  block: RichBlock;
  onHover: (k: string | null) => void;
}> = ({ block, onHover }) => {
  switch (block.type) {
    case 'metric':
      return <MetricCard block={block} onHover={onHover} />;
    case 'table':
      return <TableCard block={block} onHover={onHover} />;
    case 'timeline':
      return <TimelineCard block={block} onHover={onHover} />;
    case 'flow':
      return <FlowCard block={block} onHover={onHover} />;
    case 'text':
      return <div className={styles.richText}>{block.content}</div>;
    default:
      return null;
  }
};

// Default commands

const DEFAULT_COMMANDS: SlashCommand[] = [
  {
    command: '/compare',
    label: 'Compare',
    description: 'Compare two periods or facilities',
  },
  {
    command: '/forecast',
    label: 'Forecast',
    description: 'Project metrics forward in time',
  },
  {
    command: '/alert',
    label: 'Alert',
    description: 'Set a threshold alert for a metric',
  },
  {
    command: '/summary',
    label: 'Summary',
    description: 'Generate an executive summary',
  },
  {
    command: '/export',
    label: 'Export',
    description: 'Export analysis to PDF or Excel',
  },
];

const DEFAULT_SUGGESTIONS = [
  'What is the current status of my facility?',
  'Show me upcoming coupon payments',
  'What are the covenant metrics?',
  'Explain the facility structure',
];

export const AskDebita: FC<AskDebitaProps> = ({
  open,
  onClose,
  onSend,
  children,
  initialMessages = [],
  placeholder = 'Ask about this facility...',
  contextLabel,
  insights = [],
  onDismissInsight,
  commands = DEFAULT_COMMANDS,
  autoSendMessage,
}) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeHighlight, setActiveHighlight] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [selectionRect, setSelectionRect] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [showCommands, setShowCommands] = useState(false);
  const [commandFilter, setCommandFilter] = useState('');
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  const highlightCtx = useMemo<HighlightCtx>(
    () => ({
      activeHighlight,
      setActiveHighlight,
      selectedText,
      selectionRect,
    }),
    [activeHighlight, selectedText, selectionRect]
  );

  useEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current)
      setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  const autoSendRef = useRef(autoSendMessage);
  useEffect(() => {
    if (autoSendMessage && autoSendMessage !== autoSendRef.current && open) {
      autoSendRef.current = autoSendMessage;
      handleSend(autoSendMessage);
    } else if (autoSendMessage && !open) {
      autoSendRef.current = autoSendMessage;
    }
  }, [autoSendMessage, open]);

  // Cmd/Ctrl+K toggles the panel (`onClose` is the toggle callback).
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Select-to-Ask: detect text selection on the facility page
  useEffect(() => {
    if (!mainRef.current) return;

    const handler = () => {
      const sel = window.getSelection();
      const text = sel?.toString().trim();
      if (
        text &&
        text.length > 2 &&
        mainRef.current?.contains(sel?.anchorNode || null)
      ) {
        const range = sel!.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const mainRect = mainRef.current!.getBoundingClientRect();
        setSelectedText(text);
        setSelectionRect({
          top: rect.top - mainRect.top + rect.height + 6,
          left: rect.left - mainRect.left + rect.width / 2,
        });
      } else {
        setSelectedText(null);
        setSelectionRect(null);
      }
    };

    document.addEventListener('mouseup', handler);
    return () => document.removeEventListener('mouseup', handler);
  }, []);

  const handleSelectToAsk = () => {
    if (!selectedText) return;
    const q = `Tell me about: "${selectedText}"`;
    setSelectedText(null);
    setSelectionRect(null);
    if (!open) onClose(); // onClose toggles: opens the panel
    handleSend(q);
  };

  // Command palette
  const filteredCommands = useMemo(() => {
    if (!commandFilter) return commands;
    return commands.filter(
      c =>
        c.command.includes(commandFilter.toLowerCase()) ||
        c.label.toLowerCase().includes(commandFilter.toLowerCase())
    );
  }, [commands, commandFilter]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    if (val === '/') {
      setShowCommands(true);
      setCommandFilter('');
    } else if (val.startsWith('/') && !val.includes(' ')) {
      setShowCommands(true);
      setCommandFilter(val);
    } else {
      setShowCommands(false);
    }
  };

  const handleCommandSelect = (cmd: SlashCommand) => {
    setInput(cmd.command + ' ');
    setShowCommands(false);
    inputRef.current?.focus();
  };

  const handleSend = async (text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;

    const userMsg: Message = {
      id: uid(),
      role: 'user',
      content: msg,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setShowCommands(false);
    setLoading(true);

    try {
      const reply = await onSend(msg);
      const content: string | RichBlock[] =
        typeof reply === 'string' ? reply : reply;
      const assistantMsg: Message = {
        id: uid(),
        role: 'assistant',
        content,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch {
      const errorMsg: Message = {
        id: uid(),
        role: 'assistant',
        content: "Sorry, I couldn't process that request. Please try again.",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      setShowCommands(false);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const unreadInsights = insights.length;

  const renderContent = (content: string | RichBlock[]) => {
    if (typeof content === 'string')
      return <div className={styles.richText}>{content}</div>;

    // Group consecutive metrics into a row
    const groups: (RichBlock | RichBlock[])[] = [];
    let metricBuffer: RichMetric[] = [];

    content.forEach(b => {
      if (b.type === 'metric') {
        metricBuffer.push(b);
      } else {
        if (metricBuffer.length > 0) {
          groups.push([...metricBuffer]);
          metricBuffer = [];
        }
        groups.push(b);
      }
    });
    if (metricBuffer.length > 0) groups.push([...metricBuffer]);

    return (
      <>
        {groups.map((g, i) => {
          if (Array.isArray(g)) {
            return (
              <div key={i} className={styles.metricsRow}>
                {(g as RichMetric[]).map((m, j) => (
                  <BlockRenderer
                    key={j}
                    block={m}
                    onHover={setActiveHighlight}
                  />
                ))}
              </div>
            );
          }
          return (
            <BlockRenderer
              key={i}
              block={g as RichBlock}
              onHover={setActiveHighlight}
            />
          );
        })}
      </>
    );
  };

  return (
    <HighlightContext.Provider value={highlightCtx}>
      <div className={`${styles.splitLayout} ${open ? styles.panelOpen : ''}`}>
        {/* Main content (left) */}
        <div className={styles.mainContent} ref={mainRef}>
          {children}

          {/* Select-to-Ask tooltip */}
          {selectedText && selectionRect && (
            <button
              className={styles.selectToAsk}
              style={{ top: selectionRect.top, left: selectionRect.left }}
              onMouseDown={e => {
                e.preventDefault();
                handleSelectToAsk();
              }}
            >
              <DebitaLogo size={12} />
              <span>Ask Debita</span>
            </button>
          )}

          {/* Toggle tab */}
          <button
            className={`${styles.toggleTab} ${open ? styles.open : ''}`}
            onClick={onClose}
            aria-label={open ? 'Close Ask Debita' : 'Open Ask Debita'}
          >
            <span className={styles.toggleTabIcon}>
              <DebitaLogo size={14} />
            </span>
            {!open && <span className={styles.toggleTabLabel}>Ask</span>}
            {!open && unreadInsights > 0 && (
              <span className={styles.insightBadge}>{unreadInsights}</span>
            )}
            {open ? <IconChevronRight /> : <IconChevronLeft />}
          </button>
        </div>

        {/* AI panel (right) */}
        <aside className={styles.aiPanel}>
          {/* Header */}
          <div className={styles.panelHeader}>
            <div className={styles.panelHeaderLeft}>
              <div className={styles.panelLogo}>
                <DebitaLogo size={16} />
              </div>
              <div className={styles.panelHeaderText}>
                <span className={styles.panelTitle}>Ask Debita</span>
                {contextLabel && (
                  <span className={styles.panelContext}>{contextLabel}</span>
                )}
              </div>
            </div>
            <div className={styles.panelHeaderRight}>
              {unreadInsights > 0 && (
                <button
                  className={`${styles.insightsToggle} ${insightsExpanded ? styles.active : ''}`}
                  onClick={() => setInsightsExpanded(!insightsExpanded)}
                >
                  <IconBolt />
                  <span>{unreadInsights}</span>
                </button>
              )}
              <button
                className={styles.panelCollapseBtn}
                onClick={onClose}
                aria-label="Collapse"
              >
                <IconChevronRight />
              </button>
            </div>
          </div>

          {/* Proactive insights tray */}
          {insightsExpanded && insights.length > 0 && (
            <div className={styles.insightsTray}>
              <div className={styles.insightsTrayHeader}>
                <IconBolt />
                <span>Insights detected</span>
              </div>
              {insights.map(ins => (
                <div
                  key={ins.id}
                  className={`${styles.insightCard} ${styles[`insight_${ins.severity}`]}`}
                  onMouseEnter={() =>
                    ins.highlightKey && setActiveHighlight(ins.highlightKey)
                  }
                  onMouseLeave={() => setActiveHighlight(null)}
                >
                  <div className={styles.insightHeader}>
                    <span
                      className={`${styles.insightDot} ${STATUS_CLASSES[ins.severity]}`}
                    />
                    <span className={styles.insightTitle}>{ins.title}</span>
                    {onDismissInsight && (
                      <button
                        className={styles.insightDismiss}
                        onClick={() => onDismissInsight(ins.id)}
                      >
                        <IconX />
                      </button>
                    )}
                  </div>
                  <div className={styles.insightDetail}>{ins.detail}</div>
                  {ins.action && (
                    <button
                      className={styles.insightAction}
                      onClick={() => {
                        setInsightsExpanded(false);
                        handleSend(ins.action);
                      }}
                    >
                      Ask about this
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Messages */}
          <div className={styles.messages} ref={scrollRef}>
            {messages.length === 0 && !loading && (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <DebitaLogo size={24} />
                </div>
                <div className={styles.emptyTitle}>How can I help?</div>
                <div className={styles.emptyDesc}>
                  Ask about metrics, payments, covenants, or select anything on
                  the page.
                </div>
                <div className={styles.emptyHint}>
                  <span className={styles.kbd}>⌘K</span> to toggle &middot;{' '}
                  <span className={styles.kbd}>/</span> for commands
                </div>
                <div className={styles.suggestions}>
                  {DEFAULT_SUGGESTIONS.map(q => (
                    <button
                      key={q}
                      className={styles.suggestionChip}
                      onClick={() => handleSend(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map(msg => (
              <div
                key={msg.id}
                className={`${styles.message} ${styles[msg.role]}`}
              >
                {msg.role === 'assistant' && (
                  <div className={styles.avatarDot}>
                    <DebitaLogo size={12} />
                  </div>
                )}
                <div className={styles.bubble}>
                  {renderContent(msg.content)}
                </div>
              </div>
            ))}

            {loading && (
              <div className={`${styles.message} ${styles.assistant}`}>
                <div className={styles.avatarDot}>
                  <DebitaLogo size={12} />
                </div>
                <div className={styles.bubble}>
                  <div className={styles.thinkingBar}>
                    <span>Analyzing facility data</span>
                    <div className={styles.thinkingDots}>
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Command palette */}
          {showCommands && (
            <div className={styles.commandPalette}>
              {filteredCommands.map(cmd => (
                <button
                  key={cmd.command}
                  className={styles.commandItem}
                  onClick={() => handleCommandSelect(cmd)}
                >
                  <span className={styles.commandSlash}>
                    <IconSlash />
                  </span>
                  <div>
                    <div className={styles.commandLabel}>{cmd.label}</div>
                    <div className={styles.commandDesc}>{cmd.description}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className={styles.inputBar}>
            <textarea
              ref={inputRef}
              className={styles.textarea}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={1}
              disabled={loading}
            />
            <button
              className={`${styles.sendBtn} ${input.trim() ? styles.active : ''}`}
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              aria-label="Send"
            >
              <IconSend />
            </button>
          </div>

          <div className={styles.footer}>
            <span className={styles.kbd}>/</span> commands
          </div>
        </aside>
      </div>
    </HighlightContext.Provider>
  );
};

AskDebita.displayName = 'AskDebita';
